/**
 *
 * Employee Details Page
 *
 */
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  Descriptions,
  Dropdown,
  Menu,
  PageHeader,
  Tag,
  Modal,
  notification,
} from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, RouteComponentProps } from 'react-router';

import { RouteConstants } from 'app/routes';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { useArchiveEmployeeMutation, useGetEmployeeByIdQuery } from 'app/api';
import { MainContainer } from 'app/components/LayoutComponents/LayoutComponents';
import { EllipsisIcon } from 'app/components/CommonIcons/CommonIcons';
import { useGlobalSlice } from 'app/containers/Global/slice';
import EmployeeDrawer from '../../parts/EmployeeDrawer/EmployeeDrawer';
import PermissionsWrapper from 'app/components/PermissionsWrapper/PermissionsWrapper';
import { PermissionsEnum } from 'app/containers/User/slice/types';

interface IReactRouterParams {
  employeeId: string;
  companyId: string;
}

interface IProps extends RouteComponentProps<IReactRouterParams> {}

const EmployeeDetailsPage: FC<IProps> = ({ history, match: { params } }) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const globalActions = useGlobalSlice().actions;

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [
    archiveEmployee,
    { isLoading: isArchiveLoading },
  ] = useArchiveEmployeeMutation();

  const envId = useSelector(selectSelectedEnvId);

  const employeeId = Number(params.employeeId);
  const companyId = Number(params.companyId);

  const { data: employee, isFetching } = useGetEmployeeByIdQuery(
    { envId: envId as number, employeeId, companyId },
    { skip: !envId || !employeeId || !companyId },
  );

  const onArchive = () => {
    Modal.confirm({
      title: t(
        'Employee.ArchiveConfirm',
        'Are you sure you want to archive this employee?',
      ),
      onOk: onArchiveConfirm,
      okText: t('Common.Yes', 'Yes'),
      cancelText: t('Common.No', 'No'),
    });
  };

  const goBackToList = () => {
    history.push(
      generatePath(RouteConstants.garage.employeeList, {
        envId: envId as number,
      }),
    );
  };

  const onArchiveConfirm = async () => {
    try {
      await archiveEmployee({
        envId: envId as number,
        companyId,
        employeeId,
      }).unwrap();

      goBackToList();

      notification.success({
        message: t('Employee.ArchivedSuccessfully', {
          defaultValue: 'Employee {{firstName}} archived successfully',
          firstName: employee?.firstName,
        }),
        placement: 'bottomRight',
      });
    } catch (error) {
      dispatch(globalActions.handleError(error));
    }
  };

  return (
    <>
      <PageHeader
        onBack={goBackToList}
        className="page-header"
        title={employee?.firstName}
      />
      <MainContainer>
        <Card
          style={{ width: '100%', minHeight: 'calc(100vh - 190px)' }}
          title={t('Transport.Information', 'Information')}
          loading={isFetching || isArchiveLoading}
          extra={[
            <PermissionsWrapper
              requiredEditPermissions={[PermissionsEnum.garageAdministration]}
              key="add"
            >
              <Dropdown
                trigger={['click']}
                overlay={
                  <Menu>
                    <Menu.Item onClick={() => setIsDrawerOpen(true)}>
                      {t('Transport.ChangeInformation', 'Change information')}
                    </Menu.Item>

                    <Menu.Item onClick={onArchive}>
                      {t('Common.Archive', 'Archive')}
                    </Menu.Item>
                  </Menu>
                }
              >
                <Button type="primary" shape="circle">
                  <EllipsisIcon size={14} />
                </Button>
              </Dropdown>
            </PermissionsWrapper>,
          ]}
        >
          <Descriptions bordered column={2}>
            <Descriptions.Item label={t('Employee.Name', 'Name Surname')}>
              {employee?.firstName ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('Employee.Year', 'Year of birth')}>
              {employee?.birthYear ?? '-'}
            </Descriptions.Item>

            <Descriptions.Item
              span={2}
              label={t('Employee.PhoneNumber', 'Phone number')}
            >
              {employee?.phoneNumber ?? '-'}
            </Descriptions.Item>

            <Descriptions.Item label={'SLIM ID'}>
              {employee?.gpsEmployee?.name ?? '-'}
            </Descriptions.Item>

            <Descriptions.Item label={t('Employee.Workplace', 'Workplace')}>
              {employee?.company.name}
            </Descriptions.Item>

            <Descriptions.Item
              span={2}
              label={t('Employee.BasicSalary', 'Basic salary')}
            >
              {employee?.salary ?? '-'}
            </Descriptions.Item>

            {employee?.accountingMethods.map(accountingMethod => (
              <React.Fragment key={accountingMethod.id}>
                <Descriptions.Item
                  span={2}
                  label={t('Transport.CalculationMethod', 'Calculation method')}
                >
                  <Tag>{accountingMethod.name}</Tag>
                </Descriptions.Item>
                <Descriptions.Item
                  span={2}
                  label={t('Transport.CostPrice', 'Cost price')}
                >
                  {accountingMethod.costPrice ?? '-'}
                </Descriptions.Item>
                <Descriptions.Item
                  span={2}
                  label={t('Transport.SellPrice', 'Sell price')}
                >
                  {accountingMethod.sellPrice ?? '-'}
                </Descriptions.Item>
              </React.Fragment>
            ))}
          </Descriptions>
        </Card>
      </MainContainer>
      <EmployeeDrawer
        employee={employee}
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
      />
    </>
  );
};

export default EmployeeDetailsPage;
